import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { VideoServerCamera, DeviceType } from 'app/models/camera.model';
import { Device } from 'app/models/device.model';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IGroupedOption, MultiselectDropdownComponent } from 'app/components/multiselect-dropdown/multiselect-dropdown.component';
import { BehaviorSubject } from 'rxjs';
import { FeatureEnabledPipe } from 'app/pipes/pipes';
import { IUser } from 'app/models/user.model';

interface ISelectedLocation { id: string, name: string };

@Component({
    selector: 'app-select-cameras',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        MatOptionModule,
        MatSelectModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatSlideToggleModule,
        MatTooltipModule,
        MultiselectDropdownComponent,
        FeatureEnabledPipe
    ],
    templateUrl: './select-cameras.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    styleUrls: ['./select-cameras.component.css']
})
export class SelectCamerasComponent implements OnChanges {
  @Input() cameras: VideoServerCamera[];
  @Input() camerasOfLocation: IGroupedOption[];
  @Input() cameraList: Device[];
  @Input() selectedLocation: ISelectedLocation;
  @Input() disabled: BehaviorSubject<boolean>;
  @Input() accessMode: undefined | "visitOnAlert" | "always";
  @Input() deviceType: DeviceType;

  @Output() cameraSelectChange = new EventEmitter<VideoServerCamera[]>();
  @Output() visitWithAlertToggle = new EventEmitter<[boolean, VideoServerCamera]>();

  selectedCameraCheckboxes: string[] = [];
  user: IUser = JSON.parse(localStorage.getItem('user'));

  toggleMotionDetection(event: any, camera: VideoServerCamera): void {
    const isEnabled = event.target.checked;
    camera.motionDetection.enabled = isEnabled;

    // Clear the sub-options if the main option is disabled
    if (!isEnabled) {
      camera.motionDetection.snapshot = false;
      camera.motionDetection.videoclip = false;
      camera.motionDetection.alertVisit = false;
      this.visitWithAlertToggle.emit([false, camera]);
    }
  }

  toggleVisitWithAlert(event: any, camera: VideoServerCamera): void {
    const isEnabled = event.target.checked;
    camera.motionDetection.alertVisit = isEnabled;

    if (isEnabled) {
      camera.motionDetection.snapshot = false;
      camera.motionDetection.videoclip = false;
    }
    this.visitWithAlertToggle.emit([isEnabled, camera]);
  }

  toggleAnonymizedVisit(event: any, camera: VideoServerCamera): void {
    const isEnabled = event.target.checked;
    camera.anonymization = isEnabled;

    if (!isEnabled) {
      camera.toggleAnonymization = false;
    }
  }

  onCameraSelectedChange(cameraIds: string[]): void {
    const selectedIds = cameraIds;

    // Get the cameras that are not already selected
    const newCameraIds = selectedIds.filter(x => !this.cameras.some(y => y.discoveryName === x));

    // Remove the cameras that are not selected anymore
    this.cameras = this.cameras.filter(x => selectedIds.includes(x.discoveryName));

    for (const cameraId of newCameraIds) {
      const cameraName = this.cameraList.find(x => x.id === cameraId)?.name || "";

      const camera = this.createVideoServerCamera(cameraId, cameraName);
      this.cameras.push(camera);
    }

    this.cameraSelectChange.emit(this.cameras);
  }

  createVideoServerCamera(cameraId: string, cameraName: string): VideoServerCamera {
    return {
      deviceId: "",
      displayName: cameraName,
      discoveryName: cameraId,
      anonymization: false,
      toggleAnonymization: false,
      restingPositionEnabled: false,
      motionDetection: {
        enabled: false,
        alertVisit: false,
        snapshot: false,
        videoclip: false,
      },
    }
  }

  isNightowlDevice(): boolean {
    return this.deviceType === DeviceType.Nightowl;
  }

  ngOnChanges() {
    const selectedCameraCheckboxes = [];

    // Set checkboxes checked based on the cameras that are already included in the group
    for (const camera of this.cameras) {
      // Resting position needs to be disabled for always access cameras
      if (this.accessMode === "always") {
        camera.restingPositionEnabled = false;
      }

      selectedCameraCheckboxes.push(camera.discoveryName);
    }

    this.selectedCameraCheckboxes = [...selectedCameraCheckboxes];
  }
}
